import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import './Promotion.css';
import { LoadingContext } from '../LoadingContext';
import { AuthContext } from '../AuthContext';
import { getProductPath } from '../productUrls';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Promotion() {
  const [redeemableProducts, setRedeemableProducts] = useState([]);
  const [promoProducts, setPromoProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { setIsLoading } = useContext(LoadingContext);
  const { isLoggedIn, userInfo } = useContext(AuthContext);

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      setLoading(true);
      try {
        const [redeemRes, promoRes] = await Promise.all([
          fetch(`${API_BASE_URL}/Products/redeemable`),
          fetch(`${API_BASE_URL}/api/Promotions`),
        ]);
        const redeemData = redeemRes.ok ? await redeemRes.json() : {};
        const promoData = promoRes.ok ? await promoRes.json() : [];
        setRedeemableProducts(redeemData.products || []);
        setPromoProducts(Array.isArray(promoData) ? promoData : []);
      } catch (error) {
        setRedeemableProducts([]);
        setPromoProducts([]);
      } finally {
        setIsLoading(false);
        setLoading(false);
      }
    };
    fetchAll();
  }, [setIsLoading]);

  const getStockStatus = (product) => {
    const stock = product.stockQuantity;
    if (stock === null || stock === undefined || stock <= 0) {
      return { label: 'Out of Stock', className: 'out-of-stock' };
    }
    if (stock <= 5) {
      return { label: `${stock} Left`, className: 'low-stock' };
    }
    return { label: 'In Stock', className: 'in-stock' };
  };

  const getPromoImageUrl = (item) => item.imageUrl || null;

  const calcDiscount = (original, promo) => {
    if (!original || original <= 0) return null;
    const d = Math.min(99, Math.round((1 - promo / original) * 100));
    return d > 0 ? d : null;
  };

  return (
    <div className="promotion-main-container">
      {/* Hero Section */}
      <div className="promotion-header-section">
        <h1 className="promotion-header-title">🎁 Exclusive Promotions</h1>
        <p className="promotion-header-subtitle">
          Special deals and reward redemptions just for you!
        </p>
        {isLoggedIn && userInfo && (
          <div className="promotion-points-badge">
            <i className="fas fa-star"></i>
            Your Points: {userInfo.points?.toLocaleString() || 0}
          </div>
        )}
      </div>

      <div className="promotion-content">
        {!isLoggedIn && (
          <div className="promotion-login-cta">
            <p>Log in to see your point balance and redeem products!</p>
            <Link to="/login" className="promotion-login-btn">
              Login / Register
            </Link>
          </div>
        )}

        {loading && (
          <div className="promotion-loading">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading promotions...</p>
          </div>
        )}

        {/* Sale Products Section */}
        {!loading && promoProducts.length > 0 && (
          <div className="promotion-section">
            <div className="promotion-section-header">
              <h2 className="promotion-section-title">
                <i className="fas fa-tags"></i> Special Offers
              </h2>
              <p className="promotion-section-subtitle">Products at exclusive promotional prices</p>
            </div>
            <div className="promotion-product-grid">
              {promoProducts.map((item) => {
                const imgUrl = getPromoImageUrl(item);
                const discount = calcDiscount(item.originalPrice, item.promotionPrice);

                // Linked promotions open the catalogue product; standalone ones are not
                // catalogue products, so they open their own promotion detail page.
                const isLinked = !item.isStandalone && item.productCode;
                const CardTag = Link;
                const cardProps = isLinked
                  ? { to: getProductPath(item.productCode) }
                  : { to: `/promotion/${item.id}` };

                return (
                  <CardTag
                    {...cardProps}
                    key={item.id}
                    className="promotion-product-card"
                  >
                    <div className="promotion-product-image-container">
                      <img
                        src={imgUrl || ''}
                        alt={item.productName}
                        className="promotion-product-image"
                        style={{ display: imgUrl ? 'block' : 'none' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      {!imgUrl && (
                        <div className="promotion-product-no-image"><i className="fas fa-image"></i></div>
                      )}
                      {discount && (
                        <div className="promotion-sale-tag">
                          -{discount}%
                        </div>
                      )}
                    </div>
                    <div className="promotion-product-info">
                      <h3 className="promotion-product-name">{item.productName}</h3>
                      <div className="promotion-product-footer">
                        <div className="promotion-price-group">
                          {item.originalPrice != null && (
                            <span className="promotion-original-price">
                              RM {parseFloat(item.originalPrice).toFixed(2)}
                            </span>
                          )}
                          <span className="promotion-sale-price">
                            RM {parseFloat(item.promotionPrice).toFixed(2)}
                          </span>
                          {item.uom && <span className="promotion-uom">/ {item.uom}</span>}
                        </div>
                        <div className="promotion-view-btn">View</div>
                      </div>
                    </div>
                  </CardTag>
                );
              })}
            </div>
          </div>
        )}

        {/* Redeemable Products Section */}
        {!loading && redeemableProducts.length > 0 && (
          <div className="promotion-section">
            <div className="promotion-section-header">
              <h2 className="promotion-section-title">
                <i className="fas fa-star"></i> Redeem with Points
              </h2>
              <p className="promotion-section-subtitle">Use your reward points to claim these exclusive products</p>
            </div>
            <div className="promotion-product-grid">
              {redeemableProducts.map((product) => {
                const stockStatus = getStockStatus(product);
                return (
                  <Link
                    to="/points"
                    key={product.productID}
                    className="promotion-product-card"
                  >
                    <div className="promotion-product-image-container">
                      <img
                        src={product.mainImageUrl}
                        alt={product.name}
                        className="promotion-product-image"
                      />
                      <div className="promotion-points-tag">
                        <i className="fas fa-star"></i>
                        {product.pointsRequired?.toLocaleString()} pts
                      </div>
                      <div className={`promotion-stock-badge ${stockStatus.className}`}>
                        {stockStatus.label}
                      </div>
                    </div>
                    <div className="promotion-product-info">
                      <h3 className="promotion-product-name">{product.name}</h3>
                      <p className="promotion-product-description">
                        {product.description || 'Redeem this exclusive product with your reward points.'}
                      </p>
                      <div className="promotion-product-footer">
                        <div className="promotion-product-points">
                          {product.pointsRequired?.toLocaleString()}
                          <span>points</span>
                        </div>
                        <div className="promotion-view-btn">Redeem</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && promoProducts.length === 0 && redeemableProducts.length === 0 && (
          <div className="promotion-empty">
            <i className="fas fa-gift"></i>
            <h3>No promotions available</h3>
            <p>Check back soon for exciting new offers!</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Promotion;
